import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import MDVIPCallToAction from '../components/MDVIPCallToAction';
import AnimatedPage from '../components/AnimatedPage';

const AnnualWellnessTesting = () => {
  return (
    <AnimatedPage className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Annual Wellness Testing
            </h1>
            <div className="h-1 w-24 bg-orange-500 mx-auto"></div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Introduction */}
            <div className="mb-8">
              <p className="text-lg text-stone-700 leading-relaxed mb-6">
                The services included in the MDVIP Wellness Program will be completed in two visits. 
                The first visit will consist of lab draws, screenings and diagnostic tests that will 
                require about 45-60 minutes to perform. Your follow-up visit to discuss your results 
                and wellness plan for the year will take place about 10-14 days from your first visit 
                and will be scheduled for at least an hour.
              </p>
              <p className="text-lg text-stone-700 leading-relaxed">
                It will be necessary for you to fast the evening prior to your first visit, but please 
                continue to drink unsweetened clear fluids that evening and the morning of your appointment.
              </p>
            </div>

            {/* Two Column Layout: Additional Preparations & Tests */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: Additional Preparations */}
              <Card className="border-orange-200 shadow-md h-fit">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-stone-900 mb-4 text-orange-600">
                    Additional preparations:
                  </h2>
                  <ul className="space-y-3 text-lg text-stone-700">
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1">•</span>
                      <span>Eat a normal dinner the evening before your appointment.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1">•</span>
                      <span>Do not eat seafood the evening before your appointment (it interferes with one of the blood tests).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1">•</span>
                      <span>Do not eat any food after dinner the night before your appointment.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-orange-500 mr-3 mt-1">•</span>
                      <span>Drink plenty of fluids the night before and the morning of your appointment.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Right Column: Tests Section */}
              <Card className="border-stone-200 shadow-md">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-stone-900 mb-6 text-orange-600">
                  The following tests will be performed:
                </h2>
                <div className="space-y-6 text-stone-700">
                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Blood draw:</h3>
                    <p className="leading-relaxed">
                      We will obtain a blood sample to perform a number of tests focused on determining 
                      your risk for heart disease, diabetes and other key conditions. These studies are 
                      sent to Cleveland HeartLab for testing.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Hearing screen</h3>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Vision screen</h3>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Spirometry:</h3>
                    <p className="leading-relaxed">
                      This test measures lung function and is a good screener for asthma and COPD.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Body Composition Analysis:</h3>
                    <p className="leading-relaxed">
                      We use a special scale that measures weight, body mass index, percent body fat 
                      and basal metabolic rate.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Ankle-Brachial Index Study:</h3>
                    <p className="leading-relaxed">
                      This test is a screening tool for peripheral vascular disease. We will place 
                      blood pressure cuffs on your upper arms and ankles and measure systolic blood 
                      pressure as well as blood flow to the feet.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Electrocardiogram:</h3>
                    <p className="leading-relaxed">
                      This test measures your heart rate and rhythm.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">Agility Assessment:</h3>
                    <p className="leading-relaxed">
                      This test is a screening tool for physical capacity. A hand dynamometer is used 
                      to measure grip strength, and a walking test is performed to measure gait speed.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </section>

      {/* MDVIP CTA */}
      <MDVIPCallToAction />
    </AnimatedPage>
  );
};

export default AnnualWellnessTesting;
